import { computed, inject, Service, signal } from '@angular/core';
import Keycloak from 'keycloak-js';

import { STANDALONE_RUNTIME_CONFIG } from '../runtime/config';

type AuthSnapshot = Readonly<{
  authenticated: boolean;
  token: string | undefined;
  username: string | undefined;
}>;

const UNAUTHENTICATED_SNAPSHOT: AuthSnapshot = Object.freeze({
  authenticated: false,
  token: undefined,
  username: undefined,
});

// See the comment at its one call site (initialize()) for why this exists.
const SILENT_SSO_TIMEOUT_MS = 5000;

/**
Standalone-only Keycloak-backed authentication.

In federated mode the Shell owns authentication entirely and this MFE
never acquires its own token. This service is the standalone build's
equivalent: a real OIDC client against the local/deployed Keycloak,
parameterized by `STANDALONE_RUNTIME_CONFIG.keycloak` so the same build
artifact works against any environment's realm.

`STANDALONE_RUNTIME_CONFIG` is injected optionally: this service is
an application-wide `@Service()`, so the same `AppRoot`/`MainHeader` shared by both
entries resolves it in federated mode too, where that token is never
provided (see `remote-bootstrap.ts`). Without it, every method here is an
inert no-op rather than throwing — federated mode must stay unaffected.

State is held in a signal and deliberately never persisted (no
localStorage/sessionStorage) — a hard reload re-establishes the session
via Keycloak's own silent SSO check (the hidden iframe hitting
`/silent-check-sso.html`) rather than trusting a token this MFE wrote to
disk itself.
*/
@Service()
export class AuthService {
  private readonly snapshot = signal<AuthSnapshot>(UNAUTHENTICATED_SNAPSHOT);

  readonly isAuthenticated = computed(() => this.snapshot().authenticated);

  readonly username = computed(() => this.snapshot().username);

  private readonly keycloak: Keycloak | undefined;

  private initialized = false;

  constructor() {
    const standaloneConfig = inject(STANDALONE_RUNTIME_CONFIG, { optional: true });

    this.keycloak = standaloneConfig
      ? new Keycloak({
          url: standaloneConfig.keycloak.url,
          realm: standaloneConfig.keycloak.realm,
          clientId: standaloneConfig.keycloak.clientId,
        })
      : undefined;
  }

  /**
  Initializes the Keycloak client, including a silent SSO check against
  any existing Keycloak session. A no-op in federated mode (no Keycloak
  client to initialize).

  Must complete before route matching starts so `authGuard`/`roleGuard`
  see an accurate signal on the very first navigation — called from a
  `provideAppInitializer` in `main.ts`, which the Router's initial
  navigation waits on.
  */
  async initialize(): Promise<void> {
    if (this.initialized || !this.keycloak) {
      return;
    }

    this.initialized = true;

    this.keycloak.onAuthSuccess = () => {
      this.syncFromKeycloak();
    };
    this.keycloak.onAuthRefreshSuccess = () => {
      this.syncFromKeycloak();
    };
    this.keycloak.onAuthLogout = () => {
      this.snapshot.set(UNAUTHENTICATED_SNAPSHOT);
    };
    this.keycloak.onTokenExpired = () => {
      void this.keycloak?.updateToken(30).catch(() => {
        this.snapshot.set(UNAUTHENTICATED_SNAPSHOT);
      });
    };

    const initPromise = this.keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
      pkceMethod: 'S256',
      // Local Keycloak sits behind a reverse proxy (ima-frontdoor); its
      // check-session iframe endpoint 403s through that proxy. This feature
      // only detects a logout that happened in another tab/app — losing it
      // doesn't affect the actual login/token flow.
      checkLoginIframe: false,
    });

    // keycloak-js's own one-time silent-SSO check (triggered by `onLoad:
    // 'check-sso'` above) has no internal timeout: it awaits a postMessage
    // from the hidden iframe and never settles if that iframe never loads.
    // Locally, ima-frontdoor can serve the Keycloak auth endpoint with a
    // `frame-ancestors 'self'` CSP header, which blocks that iframe outright
    // (it's cross-origin from this app) — with no fallback, this promise
    // would hang forever and, since the caller (main.ts's
    // provideAppInitializer) blocks Angular bootstrap on it, the app would
    // never render anything at all. Racing it against a timeout here lets
    // bootstrap continue unauthenticated instead; authGuard then redirects
    // to Keycloak's hosted login page via a normal top-level navigation,
    // which `frame-ancestors` has no effect on.
    const timeout = new Promise<never>((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Keycloak silent SSO check timed out.'));
      }, SILENT_SSO_TIMEOUT_MS);
    });

    try {
      await Promise.race([initPromise, timeout]);
    } catch (error: unknown) {
      console.warn(
        'Keycloak silent SSO check failed or timed out; continuing unauthenticated.',
        error,
      );
    }

    this.syncFromKeycloak();
  }

  /**
  Redirects the browser to Keycloak's hosted login page. A no-op in
  federated mode.
  @param redirectUri - Where Keycloak sends the browser back to after login.
  @returns Resolves once the redirect has been initiated (the browser navigates away).
  */
  login(redirectUri: string = location.href): Promise<void> {
    return this.keycloak?.login({ redirectUri }) ?? Promise.resolve();
  }

  /**
  Redirects the browser to Keycloak's hosted logout endpoint. A no-op in
  federated mode.
  @param redirectUri - Where Keycloak sends the browser back to after logout.
  @returns Resolves once the redirect has been initiated (the browser navigates away).
  */
  logout(redirectUri: string = location.origin): Promise<void> {
    return this.keycloak?.logout({ redirectUri }) ?? Promise.resolve();
  }

  /**
  Reads the current access token, for `authInterceptor` to attach.
  @returns The current access token, or `undefined` when unauthenticated.
  */
  getToken(): string | undefined {
    return this.snapshot().token;
  }

  /**
  Checks the authenticated user's realm roles.
  @param roles - Realm role names; the user needs at least one.
  @returns `true` if the authenticated user holds any of the given realm roles.
  */
  hasAnyRole(roles: readonly string[]): boolean {
    return roles.some((role) => this.keycloak?.hasRealmRole(role) ?? false);
  }

  private syncFromKeycloak(): void {
    if (!this.keycloak) {
      return;
    }

    this.snapshot.set(
      Object.freeze({
        authenticated: this.keycloak.authenticated,
        token: this.keycloak.token,
        username: this.keycloak.tokenParsed?.['preferred_username'] as string | undefined,
      }),
    );
  }
}
